import { createStore, getValue } from '@kundinos/nanostores';
import { useStore } from '@kundinos/nanostores/react';

import * as TextileFiltrator from '@Stores/TextileFiltrator';

import type {
  ConstructorCollection,
  ConstructorColor,
  ConstructorGroupData,
  ConstructorType,
} from '@Types/Constructor';
import type { GroupId } from '@Types/Filters';
import type { OrderFabricsValue } from './typings';

const refContent = { current: null };

interface Colors {
  [key: number]: ConstructorColor;
}

export const orderFabricsStore = createStore<OrderFabricsValue>(() => {
  orderFabricsStore.set({
    loading: false,
    loaded: false,
    allCollections: [],
    selectedSamples: new Array(5).fill({ sample: null }),
  });
});

export const collectionsStore = createStore<ConstructorCollection[]>();

export const colorsStore = createStore<Colors>();

export const tagsTypesStore = createStore<Record<number, ConstructorType>>();

export const group = (groupId: GroupId) => {
  const tagsTypes = getValue(tagsTypesStore);
  const collections = getValue(collectionsStore);
  const colors = getValue(colorsStore);

  if (groupId === 'by-collection') {
    return [...collections]
      .sort((a, b) => a.sort - b.sort)
      .map((collection) => {
        const groupTags = [];
        collection.tags.typeIds.forEach((typeId) => groupTags.push(tagsTypes[typeId]));

        return {
          tags: groupTags,
          extensive: true,
          collection,
          title: `${collection.title} (${collection.material})`,
          additional: [
            collection.compound && {
              id: 1,
              name: 'Состав',
              value: collection.compound,
            },
            collection.resistance && {
              id: 2,
              name: 'Устойчивость к истиранию',
              value: collection.resistance,
            },
          ].filter(Boolean),
        };
      });
  }

  const groupsMap: Record<number, ConstructorGroupData> = {};

  collections.forEach((collection) => {
    collection.fabrics.forEach((fabric) => {
      const { colorId } = fabric.tags;
      const color = colors[colorId];
      const groupTags = [];

      collection.tags.typeIds.forEach((typeId) => groupTags.push(tagsTypes[typeId]));

      if (!color) return;

      if (!groupsMap[colorId]) {
        groupsMap[colorId] = {
          sort: color.sort,
          tags: [],
          title: color.title,
          collection: {
            ...collection,
            title: color.title,
            fabrics: [],
          },
          additional: [
            collection.compound && {
              id: 1,
              name: 'Состав',
              value: collection.compound,
            },
            collection.resistance && {
              id: 2,
              name: 'Устойчивость к истиранию',
              value: collection.resistance,
            },
          ].filter(Boolean),
        };
      }

      groupsMap[colorId].collection.fabrics.push({
        ...fabric,
        description: collection.description,
        additional: [
          collection.compound && {
            id: 1,
            name: 'Состав',
            value: collection.compound,
          },
          collection.resistance && {
            id: 2,
            name: 'Устойчивость к истиранию',
            value: collection.resistance,
          },
        ].filter((p) => !!p),
        tagsColor: groupTags,
        material: collection.material,
        collection: collection.title,
        price: 49000,
      });
    });
  });

  return Object.values(groupsMap).sort((ca, cb) => ca.sort - cb.sort);
};

export const groupsStore = createStore<ConstructorGroupData[]>(() => {
  const groups = getValue(TextileFiltrator.getStores().groupsStore);
  const activeGroup = groups.find((item) => item.selected);
  const activeGroupId = activeGroup ? activeGroup.id : 'by-collection';
  groupsStore.set(group(activeGroupId));
});

export const updateTextileGroups = () => {
  const groupBy = getValue(TextileFiltrator.getStores().groupsStore);

  const textilesGroup = group(groupBy.find((item) => item.selected).id);

  groupsStore.set(textilesGroup);
};

export const useOrderFabrics = () => {
  const orderFabrics = useStore(orderFabricsStore);

  return { ...orderFabrics, refContent };
};

export const useGroups = () => {
  return useStore(groupsStore);
};
